import { Component, effect, Pipe, PipeTransform, inject, ChangeDetectionStrategy } from '@angular/core';
import { ToolsMissingDoctorRecordsPageStore } from './tools-missing-doctor-records-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { FilterInput } from '@it2go/data-grid';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import {
    ProcedureValidatorErrorEnum,
} from '../../../../../../../../services/src/domain/service/procedure-validation/types/procedure-validator.error-enum';
import { doctorChipField, userClientChipColumn } from '../../../../shared/components/kit/table/table.fields';
import {
    AddressDetailObject,
    FolderWorkplaceObject,
    PersonAddressObject,
    PersonGridItemObject,
    ToolsMissingDoctorRecordGridItemObject,
} from '@it2go/types';
import { toSignal } from '@angular/core/rxjs-interop';
import { getMissingRecordsFrag } from './tools-missing-doctor-records-page.query';
import { folderUri, workUri } from '../../../../../utils/router.utils';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import {
    DateScrollerInputComponent,
} from '../../../../../components/kit/input/date-scroller-input/date-scroller-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';

@Pipe({
    name: 'onlyValidWorkplaces',
})
export class OnlyValidWorkplacesPipe implements PipeTransform {

    public transform(value: FolderWorkplaceObject[], date: string): FolderWorkplaceObject[] {
        return value.filter((workplace) => {
            return (!workplace.dateFrom || new Date(workplace.dateFrom) <= new Date(date))
                && (!workplace.dateTo || new Date(workplace.dateTo) >= new Date(date));
        });
    }
}

@Component({
    selector: 'app-tools-missing-doctor-records-page',
    templateUrl: './tools-missing-doctor-records-page.component.html',
    styleUrls: ['./tools-missing-doctor-records-page.component.scss'],
    providers: [ToolsMissingDoctorRecordsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        DateScrollerInputComponent,
        OnlyValidWorkplacesPipe,
        PrimeTemplate,
        SelectInputComponent,
        TableComponent,
    ],
})
export class ToolsMissingDoctorRecordsPageComponent extends SubscriptionComponent {

    protected readonly store = inject(ToolsMissingDoctorRecordsPageStore);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);

    form = this.fb.group({
        serviceId: this.fb.control<number | null>(null),
        workplaceId: this.fb.control<number | null>(null),
        dateFrom: this.fb.control<Date | null>(new Date()),
        dateTo: this.fb.control<Date | null>(new Date()),
    });

    filters: FilterInput[][] = [];
    columns: TableColumn[] = [
        { name: 'createDoctorRecord', exportAs: null },
        {
            name: 'date',
            type: TableColumnType.Date,
        },
        {
            name: 'day',
            dataKey: 'date',
            type: TableColumnType.Day,
        },
        {
            name: 'workDay',
            dataKey: 'date',
            type: TableColumnType.Bool,
            data: (date: string) => new Date(date).getDay() % 6 !== 0,
        },
        {
            name: 'weekend',
            dataKey: 'date',
            type: TableColumnType.Bool,
            data: (date: string) => new Date(date).getDay() % 6 === 0,
        },
        {
            ...userClientChipColumn,
            name: 'personName',
            dataKey: 'folder.person',
            data: (person: PersonGridItemObject) => {
                return {
                    ...person,
                    address: this.getCurrentAddress(person.addresses),
                    birthDate: person.birthdate,
                };
            },
            commandCallback: (item: ToolsMissingDoctorRecordGridItemObject) => this.goToFolderDetail(item.folderId, item.folder?.serviceId),
            filterable: { type: 'text' },
        },
        {
            name: 'workplace',
            dataKey: null,
            filterable: { type: 'text' },
            exportAs: (item: any) => (item.folder?.workplaces || []).map((it: any) => it.name).join(', '),
        },
        {
            name: 'activeGroups',
            dataKey: 'folder.activeGroups',
            filterable: true,
            data: (item: any[]) => (item || []).map((it) => it.group?.name || '').join(', '),
        },
        doctorChipField('doctor', {
            dataKey: 'folder.anamnesis.doctor',
        }),
    ];

    protected serviceIdChange = toSignal(this.form.controls.serviceId.valueChanges);

    constructor() {
        super();
        effect(() => {
            let serviceId = this.store.globalGlobalStore.loggedUserStore.selectedServiceId();
            if (!this.store.services().find((it: any) => it.id == serviceId)) {
                serviceId = null;
            }
            this.form.patchValue({ serviceId });
        });
        effect(() => {
            this.store.globalGlobalStore.loggedUserStore.setSelectedServiceId(this.serviceIdChange()).then();
        });
        this.subs.push(
            this.form.valueChanges.subscribe((newValues) => {
                const dateFrom = newValues.dateFrom;
                const dateTo = newValues.dateTo;
                const serviceId = newValues.serviceId;
                const workplaceId = newValues.workplaceId;
                const filters = [
                    [{ column: 'error', operator: 'EQ', value: [ProcedureValidatorErrorEnum.MISSING_DOCTOR_RECORD] }],
                    serviceId ? [{ column: 'serviceId', operator: 'EQ', value: [serviceId.toString()] }] : null,
                    (dateFrom && dateTo) ? [{
                        column: 'dateRange',
                        operator: 'BETWEEN',
                        value: [dateFrom.toISOString(), dateTo.toISOString()],
                    }] : null,
                ].filter((it) => !!it);
                if (workplaceId) {
                    filters.push([{ column: 'workplaceId', operator: 'EQ', value: [workplaceId.toString()] }]);
                }
                this.filters = filters as any;
            }),
            this.form.controls.serviceId.valueChanges.subscribe((serviceId) => {
                if (serviceId) {
                    this.loadWorkplaces(serviceId);
                }
            }),
        );
    }

    openCreateDoctorRecord(item: ToolsMissingDoctorRecordGridItemObject) {
        this.router.navigateByUrl(
            workUri({ workType: WorkTypeEnum.DOCTOR_RECORD }, item.folder!.serviceId),
            {
                state: {
                    serviceId: item.folder!.serviceId,
                    personId: item.folder!.personId,
                    dateFrom: new Date(item.date),
                },
            },
        );
    }

    private goToFolderDetail(folderId: number, serviceId: number | null | undefined) {
        this.router.navigateByUrl(folderUri(folderId, serviceId));
    }

    private getCurrentAddress(addresses: PersonAddressObject[]): AddressDetailObject | null {
        const currentDate = new Date();
        return addresses.find((address) => {
            if (!address.validFrom) return false;
            const validFrom = new Date(address.validFrom);
            const validTo = address.validTo ? new Date(address.validTo) : null;
            return validFrom <= currentDate && (!validTo || validTo >= currentDate);
        })?.address ?? null;
    }

    private loadWorkplaces(serviceId: number) {
        this.store.api.getWorkplaces({
            filter: [
                [{ column: 'serviceId', operator: 'EQ', value: [serviceId.toString()] }],
                [{ column: 'dateFrom', operator: 'LTE', value: [new Date().toISOString()] }],
                [
                    { column: 'dateTo', operator: 'GTE', value: [new Date().toISOString()] },
                    { column: 'dateTo', operator: 'EMPTY', value: ['1'] },
                ],
            ],
            sorter: [{ column: 'name', direction: 'ASC' }],
            paging: { page: 1, itemsPerPage: 999 },
        });
    }

    protected readonly TimePeriodEnum = TimePeriodEnum;
    protected readonly getMissingRecordsFrag = getMissingRecordsFrag;
}
